export const AccessScope = {
  Invisible: 'Invisible',
  Visible: 'Visible',
  Internal: 'Internal',
} as const;

export type AccessScope = (typeof AccessScope)[keyof typeof AccessScope];

export const ACCESS_SCOPE_OPTIONS: { id: AccessScope; name: string }[] = [
  { id: AccessScope.Visible, name: 'Visible' },
  { id: AccessScope.Internal, name: 'Internal' },
  { id: AccessScope.Invisible, name: 'Invisible' },
];

/** Derive scope from FO/BO payloads (visibility preferred; hidden is legacy compat). */
export function resolveAccessScope(source: {
  visibility?: string | number | null;
  Visibility?: string | number | null;
  hidden?: boolean | null;
}): AccessScope {
  const raw = source.visibility ?? source.Visibility;
  const normalized =
    typeof raw === 'number'
      ? ([AccessScope.Invisible, AccessScope.Visible, AccessScope.Internal][raw] as AccessScope | undefined)
      : raw;
  if (
    normalized === AccessScope.Invisible ||
    normalized === AccessScope.Visible ||
    normalized === AccessScope.Internal
  ) {
    return normalized;
  }
  return source.hidden ? AccessScope.Invisible : AccessScope.Visible;
}

export function isHiddenCompat(scope: AccessScope): boolean {
  return scope !== AccessScope.Visible;
}

/**
 * Whether a market from FO `/info` should appear in trade UI.
 * FO already CanAccess-filters the catalog; `hidden=true` is also set for Internal
 * (legacy "not Visible"), so clients must not treat hidden as omit.
 */
export function isListedMarket(source: {
  visibility?: string | number | null;
  Visibility?: string | number | null;
  hidden?: boolean | null;
}): boolean {
  return resolveAccessScope(source) !== AccessScope.Invisible;
}

export function accessScopeStatusLabel(scope: AccessScope, fallbackStatus: string | null | undefined): string {
  if (scope === AccessScope.Invisible) return 'Invisible';
  if (scope === AccessScope.Internal) return 'Internal';
  return fallbackStatus ?? '';
}
